import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import {
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { MoneyDeltaBadge } from '../components/money-delta-badge.tsx';
import { isOpportunityInsightType, isRiskInsightType } from '../lib/insight-bucket.ts';
import { getInsightEntityName } from '../lib/insight-entity-name.ts';
import { getInsightName } from '../lib/insight-name.ts';

type InsightCardFrontProps = {
    insightData: InsightListItem;
    /** The card's action, e.g. `CardExpandAction` or `CardMainAction`. */
    children?: ReactNode;
};

/**
 * The face of an insight card: direction icon, category, money delta and insight name. Shared by
 * the log interaction dialog and the customer detail page so both cards look the same.
 *
 * The row layout is pinned rather than left to the card's container query, so the card reads the
 * same in a narrow page column as in the wider dialog.
 */
export function InsightCardFront({ insightData, children }: InsightCardFrontProps): ReactNode {
    const { t } = useTranslation();
    const { enableNeverBoughtCrossSellCopy, enableUpToPotentialLabel } = useAppConfig();

    const { insight } = insightData;
    const DirectionIcon = isRiskInsightType(insight.insight_type) ? TrendingDownIcon : TrendingUpIcon;

    return (
        <CardFront>
            <CardStart>
                <DirectionIcon />
            </CardStart>
            <CardContent className="min-w-0">
                <CardHeader className="flex-row flex-nowrap items-center gap-container">
                    <CardTitle className="block min-w-0 flex-1 truncate font-semibold">
                        {getInsightEntityName(t, insightData)}
                    </CardTitle>
                    <CardSubtitle className="order-none shrink-0 items-center text-ui-base/4 tabular-nums">
                        <MoneyDeltaBadge
                            upToLabel={
                                enableUpToPotentialLabel && isOpportunityInsightType(insight.insight_type)
                            }
                        >
                            {roundAbsToHundred(insight.absolute_number ?? 0)}
                        </MoneyDeltaBadge>
                    </CardSubtitle>
                </CardHeader>
                <CardDetail className="truncate card-expanded:text-content">
                    {getInsightName(t, insight, { neverBoughtCrossSell: enableNeverBoughtCrossSellCopy })}
                </CardDetail>
            </CardContent>
            {children}
        </CardFront>
    );
}
